import type { ReactElement } from 'react';

import { useQuery } from '@apollo/client/react';
import { Box, Chip, Skeleton } from '@mui/material';

import type { Dictionaries_Types_Enum_Enum } from '@graphql/__generated__/graphql';
import {
  GET_DICTIONARY,
  getDictionaryLabel,
} from '@modules/_shared/components/dictionary-select/DictionarySelect';
import { chipStyle } from '@modules/_shared/components/visibility-drawer/edit-modals/EditableChip';
import EditableSectionHeader from '@modules/_shared/components/visibility-drawer/sections/EditableSectionHeader';
import type { SectionProps } from '@modules/_shared/components/visibility-drawer/types';
import { useLocale } from '@shared/hooks/locale';

export type DictionarySectionCustomProps = {
  title: string;
  dictionaryType: Dictionaries_Types_Enum_Enum;
  icon?: ReactElement;
};

export type DictionarySectionProps = SectionProps &
  DictionarySectionCustomProps;

const DictionarySection = ({
  entity,
  onEdit,
  ...config
}: DictionarySectionProps) => {
  const { t } = useLocale();
  const items: string[] = (entity as any)[config.dictionaryType] ?? [];

  const { data, loading, error } = useQuery(GET_DICTIONARY, {
    variables: { type: config.dictionaryType },
    skip: !items || items.length === 0,
  });

  if (error) {
    return (
      <EditableSectionHeader title={config.title} onEdit={onEdit}>
        <Box sx={{ color: 'error.main' }}>{t('Failed to load data')}</Box>
      </EditableSectionHeader>
    );
  }

  if (!items || items.length === 0) {
    return (
      <EditableSectionHeader title={config.title} onEdit={onEdit}>
        <Box sx={{ color: 'text.secondary' }}>-</Box>
      </EditableSectionHeader>
    );
  }

  return (
    <EditableSectionHeader title={config.title} onEdit={onEdit}>
      {loading ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton
              key={`skeleton-${config.dictionaryType}-${index}`}
              variant="rounded"
              width={80 + ((index * 20) % 40)}
              height={32}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ...chipStyle }}>
          {items.map(item => {
            return (
              <Chip
                key={item}
                icon={config.icon ?? undefined}
                label={getDictionaryLabel(item, data?.dictionaries)}
              />
            );
          })}
        </Box>
      )}
    </EditableSectionHeader>
  );
};

export default DictionarySection;
